import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useDeleteDeclaration } from "../../api/declarationsApi"

interface DeclarationDeleteDialogProps {
  /** The declaration to delete, or null when the dialog is closed. */
  declarationId: string | null
  onClose: () => void
  /** Runs after a successful delete, before the dialog closes. */
  onDeleted?: () => void
}

/** Shared confirm-before-delete dialog for a declaration — one owner for the
 *  copy, the mutation wiring, and the success toast, used by both the overview
 *  list and the detail panel. */
export function DeclarationDeleteDialog({
  declarationId,
  onClose,
  onDeleted,
}: DeclarationDeleteDialogProps) {
  const { translate } = useTranslation()
  const deleteDeclaration = useDeleteDeclaration()

  const handleDelete = () => {
    if (!declarationId) return
    deleteDeclaration.mutate(declarationId, {
      onSuccess: () => {
        toast.success(translate("MC_DECL_DELETE_TOAST"))
        onDeleted?.()
        onClose()
      },
    })
  }

  return (
    <Dialog
      open={declarationId !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate("MC_DECL_DELETE_CONFIRM_TITLE")}</DialogTitle>
          <DialogDescription>
            {translate("MC_DECL_DELETE_CONFIRM_DESC")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={deleteDeclaration.isPending}
          >
            {translate("MC_DECL_BTN_CANCEL")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteDeclaration.isPending}
          >
            {deleteDeclaration.isPending && (
              <Loader2 size={14} className="animate-spin" />
            )}
            {translate("MC_DECL_DELETE_BTN")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
